import type { z } from "zod";

import { caseApi, type CreateCaseMode } from "@recobra/schemas/api/endpoints/cases";
import { SubjectAutomationRunsResponseSchema } from "@recobra/schemas/automation";
import {
  CaseDocumentRequirementsResponseSchema,
  CaseRecordSchema,
  CaseSchema,
  type AddCaseConversationImportInput,
  type CaseSectionKey,
  type CaseTerminalStep,
  CaseType,
  type DraftCaseInput,
  FinalizedCaseApiResponseSchema,
  ListCaseApiResponsesSchema,
  type ListCasesQuery,
  type ChangeCaseStepInput,
  type UpdateCaseInput,
} from "@recobra/schemas/case";
import { ListCaseHistoryResponseSchema } from "@recobra/schemas/case-history";
import { type AddCaseNoteInput } from "@recobra/schemas/note";

import { requestJson, requestVoid } from "./client";

export type UpdateLiabilityReviewInput = z.input<typeof caseApi.updateLiabilityReview.request.body>;

const listCasesSearchParams = (params: Partial<ListCasesQuery>): string => {
  const p = new URLSearchParams();
  if (params.id) p.set("id", params.id);
  if (params.limit != null) p.set("limit", String(params.limit));
  if (params.nextToken) p.set("nextToken", params.nextToken);
  if (params.q) p.set("q", params.q);
  if (params.step?.length) p.set("step", params.step.join(","));
  if (params.caseType?.length) p.set("caseType", params.caseType.join(","));
  if (params.clientId?.length) p.set("clientId", params.clientId.join(","));
  if (params.branchId?.length) p.set("branchId", params.branchId.join(","));
  if (params.includeInactive) p.set("includeInactive", "true");
  if (params.sortBy) p.set("sortBy", params.sortBy);
  if (params.sortOrder) p.set("sortOrder", params.sortOrder);

  return p.toString();
};

export const casesApi = {
  listCases: (params?: Partial<ListCasesQuery>) => {
    const qs = listCasesSearchParams(params ?? {});

    return requestJson(qs ? `/cases?${qs}` : "/cases", ListCaseApiResponsesSchema);
  },
  getCase: (caseNumber: number) =>
    requestJson(`/cases/${caseNumber}`, FinalizedCaseApiResponseSchema),
  listCaseHistory: (id: string) =>
    requestJson(`/cases/${id}/history`, ListCaseHistoryResponseSchema),
  listAutomationRuns: (id: string) =>
    requestJson(`/cases/${id}/automation-runs`, SubjectAutomationRunsResponseSchema),
  getRequirements: (id: string, caseType?: CaseType) =>
    requestJson(
      `/cases/${id}/requirements${caseType ? `?${new URLSearchParams({ caseType })}` : ""}`,
      CaseDocumentRequirementsResponseSchema,
    ),
  createCase: (input: DraftCaseInput, mode: CreateCaseMode = "complete") =>
    requestJson(
      mode === "complete" ? "/cases" : `/cases?${new URLSearchParams({ mode })}`,
      FinalizedCaseApiResponseSchema,
      {
        method: "POST",
        body: JSON.stringify(input),
      },
    ),
  updateCase: (id: string, input: UpdateCaseInput) =>
    requestJson(`/cases/${id}`, CaseRecordSchema, {
      method: "PUT",
      body: JSON.stringify(input),
    }),
  changeCaseStep: (id: string, input: ChangeCaseStepInput) =>
    requestJson(`/cases/${id}/step`, CaseRecordSchema, {
      method: "POST",
      body: JSON.stringify(input),
    }),
  addCaseNote: (id: string, input: AddCaseNoteInput) =>
    requestJson(`/cases/${id}/notes`, CaseRecordSchema, {
      method: "POST",
      body: JSON.stringify(input),
    }),
  editCaseNote: (id: string, noteId: string, input: AddCaseNoteInput) =>
    requestJson(`/cases/${id}/notes/${noteId}`, CaseRecordSchema, {
      method: "PATCH",
      body: JSON.stringify(input),
    }),
  deleteCaseNote: (id: string, noteId: string) =>
    requestJson(`/cases/${id}/notes/${noteId}`, CaseRecordSchema, {
      method: "DELETE",
    }),
  addCaseConversationImport: (id: string, input: AddCaseConversationImportInput) =>
    requestJson(`/cases/${id}/conversation-imports`, CaseRecordSchema, {
      method: "POST",
      body: JSON.stringify(input),
    }),
  deleteCase: (id: string, disposition: CaseTerminalStep = "deleted") =>
    requestVoid(`/cases/${id}?${new URLSearchParams({ disposition })}`, {
      method: "DELETE",
    }),
  analyseCase: (id: string, caseType?: CaseType) =>
    requestVoid(`/cases/${id}/analyse`, {
      method: "POST",
      body: JSON.stringify({ caseType }),
    }),
  updateLiabilityReview: (id: string, input: UpdateLiabilityReviewInput) =>
    requestJson(`/cases/${id}/liability-analysis/review`, CaseSchema, {
      method: "PATCH",
      body: JSON.stringify(input),
    }),
  markSectionReviewed: (id: string, section: CaseSectionKey) =>
    requestJson(`/cases/${id}/sections/${section}/review`, CaseRecordSchema, {
      method: "POST",
    }),
};
